/**
 * Paleta da marca Hexium — fonte única de verdade de cor.
 *
 * `palette` são as cores entregues pela marca, sem alteração. `petrol` e `violet` são as rampas
 * derivadas por tint (mistura com branco) e shade (mistura com preto), preservando o matiz —
 * nenhum tom novo foi inventado.
 *
 * POR QUE AS RAMPAS EXISTEM
 * A paleta tem 7 cores, e uma interface precisa de mais passos: hover, superfície elevada,
 * borda em dois pesos, estado desabilitado. Mais importante: **petrol e violet são escuros**,
 * então sobre o preto da marca eles falham em contraste como texto —
 *   petrol #0F445E sobre #111111 → 1.81:1  (WCAG AA exige 4.5:1)
 *   violet #4B3FA8 sobre #111111 → 2.32:1
 * No tema escuro, portanto, a cor de marca aparece como **fundo** (com texto branco) ou como
 * **tint** quando precisa ser texto. Todos os valores abaixo foram medidos, não estimados.
 *
 * CONTRASTES DE REFERÊNCIA (WCAG 2.1)
 *   branco sobre petrol .................. 10.45:1  AAA
 *   branco sobre violet ..................  8.15:1  AAA
 *   petrol sobre branco .................. 10.45:1  AAA
 *   violet sobre branco ..................  8.15:1  AAA
 *   grayDark sobre branco ................ 12.63:1  AAA
 *   grayMedium sobre branco ..............  4.48:1  apenas texto grande / ícone
 *   grayLight sobre black ................ 13.95:1  AAA
 *   petrol.tint45 sobre black ............  6.19:1  AA
 *   violet.tint45 sobre black ............  6.85:1  AA
 */

export const palette = {
  petrol: '#0F445E',   // primária — logotipo, títulos, elementos-chave
  violet: '#4B3FA8',   // secundária — acentos, CTA discreto, destaques
  black: '#111111',
  grayDark: '#333333',
  grayMedium: '#777777',
  grayLight: '#D9DEE4',
  white: '#FFFFFF',
} as const;

/** Rampa da primária. `base` é a cor da marca; shades escurecem, tints clareiam. */
export const petrol = {
  shade30: '#0B3042',
  shade15: '#0D3A50',
  base: palette.petrol,
  tint15: '#336076',
  tint30: '#577C8E',
  tint45: '#7B98A6',   // 6.19:1 sobre #111111 — é o petrol legível no tema escuro
  tint60: '#9FB4BF',
  tint75: '#C3D0D7',
  tint88: '#E2E9EC',
} as const;

/** Rampa da secundária. */
export const violet = {
  shade30: '#352C76',
  shade15: '#40368F',
  base: palette.violet,
  tint15: '#665CB5',
  tint30: '#8179C2',
  tint45: '#9C95CF',   // 6.85:1 sobre #111111 — é o violet legível no tema escuro
  tint60: '#B7B2DC',
  tint75: '#D2CFE9',
  tint88: '#E9E8F5',
} as const;

/**
 * Superfícies do tema escuro, por elevação, derivadas do preto da marca.
 * `border` coincide com o grayDark da marca — a marca já fornecia o passo certo.
 */
export const darkSurface = {
  sunken: '#0B0B0B',
  base: palette.black,   // #111111
  raised: '#1D1D1D',     // branco sobre: 16.86:1
  overlay: '#262626',    // branco sobre: 15.13:1
  border: palette.grayDark,
  borderStrong: '#3F3F3F',
} as const;

/** Superfícies do tema claro. `base` é um tint do grayLight, para dar profundidade sem tom novo. */
export const lightSurface = {
  base: '#ECEFF2',       // grayLight clareado 50%
  raised: palette.white,
  overlay: palette.white,
  border: palette.grayLight,
  borderStrong: petrol.tint75,
} as const;

/**
 * Semânticas de estado. A marca não especifica erro/sucesso, e cor de risco **não deve** variar
 * por marca — então ficam distintas de petrol/violet de propósito, apenas ajustadas para passar
 * contraste em cada tema.
 */
export const feedback = {
  errorLight: '#C62828',
  errorDark: '#F28B82',    // legível sobre #111111
  successLight: '#1B7F5A',
  successDark: '#6EDBAE',  // legível sobre #111111
} as const;

/** rgba a partir de um hex da marca, para camadas translúcidas (hover, glow, overlay). */
export const alpha = (hex: string, a: number): string => {
  const [r, g, b] = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16));
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};
